/* @flow */
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import ActionButton from '../ActionButton';
import ActionButtons from '../ActionButtons';
import Modal from '../Modal';
import Table from '../Table';
import TrackListHeader from '../TrackListHeader';
import TrackListRow from '../TrackListRow';
import WorklistBasicForm from '../../forms/WorklistBasicForm';
import actions from '../../redux/actions';
import { getWorklists, getWorklist1, getWorklist2 } from '../../redux/reducers/worklists';
import { getSessionUsageId } from '../../redux/reducers/usages';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';
import styles from './styles.module.scss';

import type {
  Fetchable,
  ReactRouterProps,
  ReduxState,
  SimplifiedTrack,
  SimplifiedWorklist,
  Track,
  Translate,
  WorklistItem,
} from '../../types';
import InputSelect from 'components/InputSelect';
import Button from 'components/Button';

type Props = ReactRouterProps & {
  tracks: Array<Track>,
  currentTrackIndex: number,
  isPlaying?: boolean,
  onTrackClick: (trackIndex: number) => mixed,
  onClearClick: () => mixed,
  worklists: Fetchable<SimplifiedWorklist[]>,
  worklist1: Fetchable<SimplifiedWorklist>,
  worklist2: Fetchable<SimplifiedWorklist>,
  sessionUsageId: Fetchable<number>,
  displayMessage: typeof actions.displayMessage,
  createWorklistItems: typeof actions.createWorklistItems,
  saveWorklistItemsForCheckout: typeof actions.saveWorklistItemsForCheckout,
  saveWorklist: typeof actions.saveWorklist,
  downloadTracks: typeof actions.downloadTracks,
  playTracks: typeof actions.playTracks,
  t: Translate,
};

type State = {
  selectedTracks: {
    [trackId: number]: boolean,
  },
  expandedTracks: {
    [trackId: number]: boolean,
  },
  isCreateWorklistModalVisible: boolean,
  isSelectWorklistModalVisible: Boolean,
  selectWorklistSelectedId: number,
  selectWorklistSessionUsageId: number,
  selectWorklistTracks: Track[],
  createWorklistWithItemsAction: ((name: string, description: string) => mixed) | null,
};

const mapStateToProps = (state: ReduxState) => {
  return {
    worklists: getWorklists(state),
    worklist1: getWorklist1(state),
    worklist2: getWorklist2(state),
    sessionUsageId: getSessionUsageId(state),
  };
};

class PlayerQueue extends React.PureComponent<Props, State> {
  state = {
    selectedTracks: {},
    expandedTracks: {},
    isCreateWorklistModalVisible: false,
    isSelectWorklistModalVisible: false,
    selectWorklistSelectedId: 0,
    createWorklistWithItemsAction: null,
    selectWorklistSessionUsageId: 0,
    selectWorklistTracks: [],
  };

  get selectedTrackIds(): number[] {
    return _.map(_.keys(_.pickBy(this.state.selectedTracks, v => v === true)), v => Number(v));
  }

  get expandedTrackIds(): number[] {
    return _.map(_.keys(_.pickBy(this.state.expandedTracks, v => v === true)), v => Number(v));
  }

  _toggleSelectTracks = (trackIds: number[]) => {
    const { selectedTracks } = this.state;
    const isAllSelected = trackIds.every(id => this.selectedTrackIds.includes(id));
    for (const trackId of trackIds) {
      selectedTracks[trackId] = !isAllSelected;
    }
    this.setState({ selectedTracks });
    this.forceUpdate();
  };

  _toggleExpandSelectedTracks = () => {
    const { expandedTracks } = this.state;
    const isAllExpanded = this.selectedTrackIds.every(id => this.expandedTrackIds.includes(id));
    for (const trackId of this.selectedTrackIds) {
      expandedTracks[trackId] = !isAllExpanded;
    }
    this.setState({ expandedTracks });
    this.forceUpdate();
  };

  _saveItemsForCheckout = (worklistItems: WorklistItem[], worklistId: number) => {
    this.props.saveWorklistItemsForCheckout(worklistItems, worklistId);
  };

  _requestCreateWorklistWithItems = (
    tracks: SimplifiedTrack[],
    usageId: number,
    shouldProceedToCheckout?: boolean = false
  ) => {
    const { saveWorklist, createWorklistItems, t } = this.props;
    const createWorklistWithItemsAction = (name: string, description: string) =>
      saveWorklist(
        {
          name: name || t('WORKLIST'),
          description: description || t('NO_DESCRIPTION'),
          usage: protonesUtils.getUsageUpdateParamsFromObject({}),
        },
        async worklist => {
          await createWorklistItems(tracks, worklist, usageId, (worklistItems: WorklistItem[]) => {
            if (shouldProceedToCheckout) {
              this._saveItemsForCheckout(worklistItems, worklist.id);
              protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklist.id));
            }
          });
        }
      );
    this._showCreateWorklistModal(createWorklistWithItemsAction);
  };

  _requestSendItemsToWorklist = async (
    tracks: SimplifiedTrack[],
    worklist: any,
    usageId: any,
    shouldProceedToCheckout?: boolean = false
  ) => {
    const { createWorklistItems } = this.props;
    await createWorklistItems(tracks, worklist, usageId, (worklistItems: WorklistItem[]) => {
      if (shouldProceedToCheckout) {
        this._saveItemsForCheckout(worklistItems, worklist.id);
        protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklist.id));
      }
    });
  };

  _showSelectWorklistModal = (worklists, tracks, sessionUsageId) => {
    this.setState({
      isSelectWorklistModalVisible: true,
      selectWorklistTracks: tracks,
      selectWorklistSessionUsageId: sessionUsageId,
    });
  };

  _showCreateWorklistModal = createWorklistWithItemsAction => {
    this.setState({ isCreateWorklistModalVisible: true, createWorklistWithItemsAction });
  };

  _dismissCreateWorklistModal = () => {
    this.setState({ isCreateWorklistModalVisible: false, createWorklistWithItemsAction: null });
  };

  _dismissSelectWorklistModal = () => {
    this.setState({ ...this.state, isSelectWorklistModalVisible: false });
  };

  _setSelectWorklistSelectedId = selectWorklistSelectedId => {
    this.setState({ selectWorklistSelectedId });
  };

  _handleCreateWorklistModalSubmit = worklistParams => {
    const { createWorklistWithItemsAction } = this.state;
    const { name, description } = worklistParams;
    this._dismissCreateWorklistModal();
    if (createWorklistWithItemsAction != null) {
      createWorklistWithItemsAction(name, description);
    }
  };

  _renderActionButtons = (tracks: Track[]) => {
    const {
      displayMessage,
      createWorklistItems,
      downloadTracks,
      sessionUsageId,
      worklist1,
      worklist2,
      worklists,
      t,
    } = this.props;
    const downloadable = tracks.every(track => track.downloadable === true);
    return (
      <ActionButtons
        buy={{
          fetchable: true,
          fetching: sessionUsageId.fetching,
          error: sessionUsageId.error,
          data: worklists.data && [
            {
              name: t('CREATE'),
            },
            {
              id: 0,
              name: t('NEW_WORKLIST'),
              // $FlowFixMe
              onClick: () =>
                this._requestCreateWorklistWithItems(tracks, sessionUsageId.data, true),
            },
            {
              name: t('ADD_TO'),
            },
            ...(worklists.data || []).map(worklist => ({
              ...worklist,
              onClick: () =>
                this._requestSendItemsToWorklist(tracks, worklist, sessionUsageId.data, true),
            })),
          ],
          tooltip: t('SEND_TO_WORKLIST_BEFORE_BUY'),
        }}
        download={{
          fetchable: downloadable,
          fetching: false,
          data: () =>
            downloadable
              ? // $FlowFixMe
                downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
        worklist1={{
          fetching: worklist1.fetching || sessionUsageId.fetching,
          error: worklist1.error || sessionUsageId.error,
          data: {
            ...worklist1.data,
            onClick: worklist1.data
              ? // $FlowFixMe
                () => createWorklistItems(tracks, worklist1.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL1'), true),
          },
        }}
        worklist2={{
          fetching: worklist2.fetching || sessionUsageId.fetching,
          error: worklist2.error || sessionUsageId.error,
          data: {
            ...worklist2.data,
            onClick: worklist2.data
              ? // $FlowFixMe
                () => createWorklistItems(tracks, worklist2.data, sessionUsageId.data)
              : () => displayMessage(t('SELECT_A_WL2'), true),
          },
        }}
        worklists={{
          fetching: worklists.fetching || sessionUsageId.fetching,
          error: worklists.error || sessionUsageId.error,
          data: {
            ...worklists.data,
            onClick: () => {
              this._showSelectWorklistModal(worklists.data, tracks, sessionUsageId.data);
            },
          },
        }}
      />
    );
  };

  render() {
    const {
      tracks,
      currentTrackIndex,
      isPlaying,
      onClearClick,
      playTracks,
      t,
      worklists,
    } = this.props;
    const {
      isCreateWorklistModalVisible,
      isSelectWorklistModalVisible,
      selectWorklistTracks,
      selectWorklistSessionUsageId,
      selectWorklistSelectedId,
    } = this.state;
    const isSelectedAllExpanded = this.selectedTrackIds.every(id =>
      this.expandedTrackIds.includes(id)
    );
    const isAllSelected = tracks.map(t => t.id).every(id => this.selectedTrackIds.includes(id));
    const isSomeSelected = this.selectedTrackIds.length > 0;
    const selectedTracks: Track[] = (tracks || []).filter(track =>
      _.includes(this.selectedTrackIds, track.id)
    );
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Table.Table style={{ height: '100%' }}>
            <thead>
              <TrackListHeader
                className={styles.trackListHeader}
                isSelectable={true}
                showCheckbox={tracks !== undefined}
                isSelected={isAllSelected}
                isPlayerVisible={selectedTracks.length > 0}
                onDetailsExpand={this._toggleExpandSelectedTracks}
                isDetailsExpanded={isSelectedAllExpanded}
                onCheckboxClick={() => this._toggleSelectTracks(tracks.map(t => t.id))}
                showActionButtons={isSomeSelected}
                onPlayClick={() => playTracks(selectedTracks)}
                actionButtons={this._renderActionButtons(selectedTracks)}
                adjustForScrollbar={true}
                rightElement={<ActionButton iconName="delete_sweep" onClick={onClearClick} />}
              />
            </thead>
            <tbody>
              {tracks.map((track, index) => {
                let isCurrentTrack = index === currentTrackIndex;
                // isCurrentTrack = index === tracks.length - 1;
                const isSelected = !!this.state.selectedTracks[track.id];
                const isExpanded = !!this.state.expandedTracks[track.id];
                return (
                  <TrackListRow
                    key={`track-${index}-${track.id}`}
                    isSelected={isSelected}
                    isSelectable={true}
                    isCurrentTrack={isCurrentTrack}
                    isPlaying={isPlaying}
                    isDetailsExpanded={isExpanded}
                    track={track}
                    medias={track.medias}
                    onPlayClick={() => playTracks([track])}
                    onCheckboxClick={() => this._toggleSelectTracks([track.id])}
                    actionButtons={this._renderActionButtons([track])}
                  />
                );
              })}
            </tbody>
          </Table.Table>
          {isCreateWorklistModalVisible && (
            <Modal
              title={stringUtils.capitalizeString(t('NEW_WORKLIST'))}
              onDismiss={this._dismissCreateWorklistModal}
            >
              <WorklistBasicForm save={this._handleCreateWorklistModalSubmit} />
            </Modal>
          )}
          {isSelectWorklistModalVisible && (
            <Modal
              title={t('ADD_TO') + ' ' + t('WORKLIST')}
              onDismiss={this._dismissSelectWorklistModal}
            >
              <InputSelect
                label={t('ADD_TO')}
                title={t('ADD_TO')}
                onChange={e => this._setSelectWorklistSelectedId(e.target.value)}
              >
                <option key={0} value={''} />
                {worklists.data.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </InputSelect>
              <div style={{ marginTop: '2em', display: 'flex', justifyContent: 'space-around' }}>
                <span />
                <Button
                  text={t('CANCEL')}
                  onClick={this._dismissSelectWorklistModal}
                  disabled={false}
                  appearance={'flat'}
                />
                <Button
                  text={t('NEW_WORKLIST')}
                  onClick={() => {
                    this._dismissSelectWorklistModal();
                    this._requestCreateWorklistWithItems(
                      selectedTracks,
                      selectWorklistSessionUsageId
                    );
                  }}
                  disabled={false}
                  appearance={'flat'}
                />
                <Button
                  text={t('ADD')}
                  onClick={() => {
                    const worklist = worklists.data.find(
                      worklist => worklist.id === parseInt(selectWorklistSelectedId)
                    );
                    this._requestSendItemsToWorklist(
                      selectWorklistTracks,
                      worklist,
                      selectWorklistSessionUsageId
                    );
                    this._dismissSelectWorklistModal();
                  }}
                  disabled={selectWorklistSelectedId === 0}
                  appearance={'primary'}
                />
              </div>
            </Modal>
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(PlayerQueue)));
